import {
    AmountSeries,
    SupplierSeries,
    SuppliersSpendVolumeResponse
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/utils/types';
import { ChartData, ChartFilters, ChartItem, HighlightAnswer, SpendVolume } from './chart-types';
import chartUtils, {
    categorySort,
    dateSort,
    mapCentsToDollars,
    segregateSuppliers,
    spendVolumeSort,
    sumInvoiceAmount
} from './chart-utils';
import { DateFormat } from '@lsq-frontend/common-ui';
import { format } from 'date-fns';
import { HighChartComponent } from '../lsq-high-charts';

const getSpendVolumeConfig = (data: SpendVolume, colors: string[]) => {
    if (data.data.length === 0) {
        return {};
    }
    const abbrevi = chartUtils.getDataLabelAbbreviator();
    const chartConfig = {
        chart: {
            type: 'column',
            height: '530px'
        },
        title: {
            text: null,
            align: 'left'
        },
        xAxis: {
            categories: data.categories,
            title: {
                text: 'Invoice Date',
                style: chartUtils.getTextStyle()
            },
            labels: {
                style: chartUtils.getTextStyle()
            }
        },
        yAxis: {
            min: 0,
            title: {
                text: 'AP Spend ($)',
                style: chartUtils.getTextStyle()
            },
            stackLabels: {
                enabled: false
            },
            labels: {
                style: chartUtils.getTextStyle()
            }
        },
        tooltip: {
            style: chartUtils.getTextStyle(),
            formatter: function (this: any) {
                const header = `<b>${this.x}</b><br/>`;
                const body =
                    (this.series.name !== 'Spend' ? '<b>' + this.series.name + '</b><br/>' : '') +
                    'AP Spend: ' +
                    chartUtils.abbreviatorFormatter(this.y) +
                    '<br/>';
                return '<div>' + header + body + '</div>';
            }
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: true
                }
            },
            series: {
                groupPadding: 0.15,
                pointPadding: 0.001
            }
        },
        exporting: {
            enabled: false
        },
        colors: colors,
        series:
            typeof data.data[0] === 'number'
                ? [
                      {
                          name: 'Spend',
                          showInLegend: false,
                          data: data.data.map(mapCentsToDollars),
                          ...abbrevi
                      }
                  ]
                : data.data.map((serie: ChartItem) => ({
                      name: serie.name,
                      showInLegend: false,
                      data: serie.data.map(mapCentsToDollars),
                      ...abbrevi
                  }))
    };
    return chartConfig;
};

export default getSpendVolumeConfig;

export const GetSpendVolumeSchema = (
    data: SuppliersSpendVolumeResponse,
    filters: ChartFilters,
    context: HighChartComponent
) => {
    if (data.suppliers.length === 0) {
        throw new Error('Empty data');
    }
    // local filtering
    const sourceData = data.suppliers
        .filter(
            (supp: SupplierSeries) =>
                filters.suppliers.includes(supp.name) || filters.suppliers.length === 0
        )
        .sort(spendVolumeSort);
    const categories = [...sourceData].sort(categorySort)[0].amountSeries.sort(dateSort);

    const formattedCategories = categories.map((serie: AmountSeries) =>
        format(serie.date, DateFormat.MonthNumberDashYear)
    );

    let earlyPaid: boolean = false;
    let chartData: ChartData = [];
    if (filters.highlighted === HighlightAnswer.Yes) {
        chartData = sourceData.map((supp: SupplierSeries) => ({
            name: supp.name,
            data: supp.amountSeries
                .sort(dateSort)
                .map((serie: AmountSeries) => serie.invoiceAmount.cents)
        }));
        const slideNumber = chartUtils.countMatchingColors(
            context.colorRegistry,
            filters.suppliers
        );
        chartData = segregateSuppliers(slideNumber, chartData);
    } else if (filters.highlighted === HighlightAnswer.No) {
        chartData = sumInvoiceAmount(categories, sourceData);
    } else if (filters.highlighted === HighlightAnswer.EarlyPaid) {
        let fundedAmount: number[] = [];
        let invoiceAmount: number[] = [];
        for (const supp of sourceData) {
            for (let [indx, slot] of supp.amountSeries.sort(dateSort).entries()) {
                fundedAmount[indx] = (fundedAmount[indx] ?? 0) + slot.fundedAmount.cents;
                invoiceAmount[indx] = (invoiceAmount[indx] ?? 0) + slot.invoiceAmount.cents;
            }
        }
        chartData = [
            {
                name: 'Invoice amount',
                data: invoiceAmount
            },
            {
                name: 'Funded amount',
                data: fundedAmount
            }
        ];
        earlyPaid = true;
    }

    const suppliers = data.suppliers.sort(spendVolumeSort).map((supp: SupplierSeries) => supp.name);
    if (!context.suppliers) {
        context.setSuppliers(suppliers);
    }

    const colors = chartUtils.filterColors(context.colorRegistry, filters.suppliers, earlyPaid);

    const schema = getSpendVolumeConfig(
        {
            data: chartData as ChartItem[],
            categories: formattedCategories
        },
        colors
    );
    return schema;
};
